import React, { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { displayCrewName, jobMatchesSelectedCrews } from '../lib/crew'

export default function AllJobsPrintView() {
  const { date } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const sortBy = searchParams.get('sort') || 'crew'
  const crewsParam = searchParams.get('crews')
  const selectedCrews = crewsParam ? new Set(crewsParam.split(',')) : null

  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let isMounted = true
    const load = async () => {
      if (!supabase) {
        if (isMounted) setLoading(false)
        return
      }
      try {
        const { data, error } = await supabase
          .from('jobs')
          .select(`id, start_time, end_time, crew_name, comments, flag, property:properties(name, address, phone, maintenance_supervisor_phone, standing_directions, property_manager_name, property_manager_phone)`)
          .eq('job_date', date)
          .neq('status', 'cancelled')
          .neq('is_deleted', true)
          .order('start_time')
        if (!error && isMounted) {
          const filtered = selectedCrews
            ? (data || []).filter(j => jobMatchesSelectedCrews(j, selectedCrews))
            : (data || [])
          // Sort client-side so midnight jobs (stored as 00:00 by Postgres) appear last
          const sorted = [...filtered].sort((a, b) => {
            const norm = t => (t === '00:00:00' || t === '00:00') ? '24:00:00' : t
            return norm(a.start_time).localeCompare(norm(b.start_time))
          })
          setJobs(sorted)
        }
      } catch (e) {
        console.warn('Could not load jobs for print:', e.message)
      } finally {
        if (isMounted) setLoading(false)
      }
    }
    load()
    return () => { isMounted = false }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, crewsParam])

  const formattedDate = date
    ? new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      })
    : ''

  // Build the header label based on what's being printed
  const headerLabel = (() => {
    if (!selectedCrews) return 'All Crews'
    const names = [...selectedCrews]
    if (names.length === 1) return `${names[0]}'s Schedule`
    if (names.length === 2) return `${names[0]} & ${names[1]}`
    return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`
  })()

  const sortedJobs = [...jobs].sort((a, b) => {
    if (sortBy === 'crew') {
      const c = displayCrewName(a.crew_name).localeCompare(displayCrewName(b.crew_name))
      return c !== 0 ? c : (a.start_time || '').localeCompare(b.start_time || '')
    }
    return (a.start_time || '').localeCompare(b.start_time || '')
  })

  const groupedJobs = sortBy === 'crew'
    ? sortedJobs.reduce((acc, job) => {
        const crew = displayCrewName(job.crew_name)
        if (!acc[crew]) acc[crew] = []
        acc[crew].push(job)
        return acc
      }, {})
    : null

  const formatNotes = (text) => {
    if (!text) return null
    return text.replace(/fwd (?=\d)/g, 'fwd\n').split('\n').map((line, i) => (
      <div key={i}>{line}</div>
    ))
  }


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading schedule...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white p-6 font-mono text-sm print-outer">
      <style>{`
        @media print {
          @page { margin: 0.5in; }
          .print-outer { padding: 0 !important; min-height: 0 !important; }
          .print-table { width: 100%; border-collapse: collapse; }
          .print-table thead tr th { font-weight: normal; text-align: left; }
          .print-header-date { float: right; font-size: 0.875rem; }
        }
      `}</style>
      {/* Controls — hidden when printing */}
      <div className="no-print mb-6 flex flex-wrap gap-3 items-center">
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700"
        >
          🖨 Print / Save PDF
        </button>
        <button
          onClick={() => { window.location.href = `/?date=${date}` }}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
        >
          Close
        </button>

        <div className="border-l border-gray-300 h-6 mx-2" />

        <span className="text-sm text-gray-600">Sort by:</span>
        <button
          onClick={() => setSearchParams({ sort: 'time', ...(crewsParam ? { crews: crewsParam } : {}) })}
          className={`px-3 py-1.5 text-sm rounded-lg ${
            sortBy === 'time'
              ? 'bg-brand-100 text-brand-700 font-medium'
              : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          Time
        </button>
        <button
          onClick={() => setSearchParams({ sort: 'crew', ...(crewsParam ? { crews: crewsParam } : {}) })}
          className={`px-3 py-1.5 text-sm rounded-lg ${
            sortBy === 'crew'
              ? 'bg-brand-100 text-brand-700 font-medium'
              : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          Crew Member
        </button>
      </div>

      <div className="max-w-5xl mx-auto">
        {jobs.length === 0 ? (
          <>
            <div className="flex justify-between items-end pb-2 mb-3 border-b-2 border-black">
              <div>
                <div className="text-xl font-bold tracking-tight">Schedule</div>
                <div className="text-base font-semibold">{headerLabel}</div>
              </div>
              <div className="text-right text-sm">{formattedDate}</div>
            </div>
            <div className="text-center py-8 text-gray-500">
              No jobs scheduled for this date.
            </div>
          </>
        ) : sortBy === 'crew' ? (
          // Crew mode: one table per crew — each starts on a new page, thead repeats if crew spans multiple pages
          <div>
            {Object.entries(groupedJobs).map(([crewName, crewJobs], idx) => (
              <div key={crewName} style={{ pageBreakBefore: idx === 0 ? 'auto' : 'always', breakBefore: idx === 0 ? 'auto' : 'page' }}>
                <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ fontWeight: 'normal', textAlign: 'left' }}>
                        {/* No flex inside thead — Chrome won't repeat header if th contains flex children */}
                        <span className="print-header-date">{formattedDate}</span>
                        <div style={{ fontSize: '1.25rem', fontWeight: 'bold', lineHeight: '1.25' }}>Schedule</div>
                        <div style={{ fontSize: '1rem', fontWeight: '600', paddingBottom: '6px', borderBottom: '2px solid black' }}>{crewName}</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {crewJobs.map((job, jobIdx) => {
                      const prop = job.property || {}
                      const directionParts = [
                        prop.property_manager_name &&
                          `Mgr: ${prop.property_manager_name}${prop.property_manager_phone ? ` ${prop.property_manager_phone}` : ''}`,
                      ].filter(Boolean)
                      return (
                        <tr key={job.id} style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                          <td style={{ padding: 0 }}>
                            <div className="py-3">
                              <div className="flex-1 grid gap-x-20" style={{ gridTemplateColumns: '2fr 2fr 1.5fr' }}>
                                <div>
                                  <div className="font-bold text-base leading-tight">{prop.name || '—'}</div>
                                  <div className="text-base mt-0.5">{prop.address}</div>
                                  {prop.maintenance_supervisor_phone && (
                                    <div className="text-xs mt-0.5">Maint: {prop.maintenance_supervisor_phone}</div>
                                  )}
                                  {prop.standing_directions && (
                                    <div className="text-xs mt-0.5">{prop.standing_directions}</div>
                                  )}
                                  {directionParts.length > 0 && (
                                    <div className="mt-1 text-xs text-gray-600">{directionParts.join(' / ')}</div>
                                  )}
                                </div>
                                <div className="text-xs pl-16">
                                  {formatNotes(job.comments)}
                                  {job.flag === 'urgent' && (
                                    <div className="mt-0.5 font-bold">⚑ URGENT</div>
                                  )}
                                </div>
                                <div className="text-xs text-right">
                                  {prop.phone && <div>Phone: {prop.phone}</div>}
                                </div>
                              </div>
                            </div>
                            {jobIdx < crewJobs.length - 1 && <hr className="border-gray-300" />}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        ) : (
          // Time mode: single table, thead repeats on every page
          <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ fontWeight: 'normal', textAlign: 'left' }}>
                  {/* No flex inside thead — Chrome won't repeat header if th contains flex children */}
                  <span className="print-header-date">{formattedDate}</span>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', lineHeight: '1.25' }}>Schedule</div>
                  <div style={{ fontSize: '1rem', fontWeight: '600', paddingBottom: '6px', borderBottom: '2px solid black' }}>{headerLabel}</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedJobs.map((job, idx) => {
                const prop = job.property || {}
                const directionParts = [
                  prop.property_manager_name &&
                    `Mgr: ${prop.property_manager_name}${prop.property_manager_phone ? ` ${prop.property_manager_phone}` : ''}`,
                ].filter(Boolean)
                return (
                  <tr key={job.id} style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                    <td style={{ padding: 0 }}>
                      <div className="py-3">
                        <div className="flex-1 grid gap-x-20" style={{ gridTemplateColumns: '2fr 2fr 1.5fr' }}>
                          <div>
                            <div className="font-bold text-base leading-tight">{prop.name || '—'}</div>
                            <div className="text-base mt-0.5">{prop.address}</div>
                            {prop.maintenance_supervisor_phone && (
                              <div className="text-xs mt-0.5">Maint: {prop.maintenance_supervisor_phone}</div>
                            )}
                            {prop.standing_directions && (
                              <div className="text-xs mt-0.5">{prop.standing_directions}</div>
                            )}
                            {directionParts.length > 0 && (
                              <div className="mt-1 text-xs text-gray-600">{directionParts.join(' / ')}</div>
                            )}
                          </div>
                          <div className="text-xs pl-16">
                            {formatNotes(job.comments)}
                            {job.flag === 'urgent' && (
                              <div className="mt-0.5 font-bold">⚑ URGENT</div>
                            )}
                          </div>
                          <div className="text-xs text-right">
                            {prop.phone && <div>Phone: {prop.phone}</div>}
                          </div>
                        </div>
                      </div>
                      {idx < sortedJobs.length - 1 && <hr className="border-gray-300" />}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
